import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = '/api';

function Landing() {
  const navigate = useNavigate();
  const [loginHash, setLoginHash] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Welcome modal state
  const [showWelcome, setShowWelcome] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  // Live network stats
  const [networkStats, setNetworkStats] = useState({
    workers_online: 0,
    total_cores: 0,
    avg_speed_ghz: 0,
    total_memory_gb: 0,
    total_core_hours: 0,
    total_cache_served: 0,
    total_accounts: 0,
    co2_today_kg: 0,
    co2_total_kg: 0,
    trees_equivalent: 0,
    car_km_equivalent: 0,
    lifetime_core_hours: 0,
    lifetime_queries: 0
  });

  // Chart data for network activity
  const [activityData, setActivityData] = useState([]);
  const chartRef = useRef(null);

  // Leaderboards
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [allianceLeaderboard, setAllianceLeaderboard] = useState([]);
  const [allianceLeaderboardLoading, setAllianceLeaderboardLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contributors');

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check session
  useEffect(() => {
    const savedHash = localStorage.getItem('avalw_account_hash');
    if (savedHash) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/stats`);
        const data = await res.json();

        const newStats = {
          workers_online: data.network?.workers_online || data.active_workers || 0,
          total_cores: data.network?.total_cores || 0,
          avg_speed_ghz: data.network?.avg_speed_ghz || 0,
          total_memory_gb: data.network?.total_memory_gb || 0,
          total_core_hours: data.total_core_hours_today || 0,
          total_cache_served: data.network?.total_cache_served || 0,
          total_accounts: data.total_accounts || 0,
          co2_today_kg: data.co2_impact?.saved_today_kg || 0,
          co2_total_kg: data.co2_impact?.saved_total_kg || 0,
          trees_equivalent: data.co2_impact?.trees_equivalent || 0,
          car_km_equivalent: data.co2_impact?.car_km_equivalent || 0,
          lifetime_core_hours: data.lifetime?.core_hours || 0,
          lifetime_queries: data.lifetime?.queries_served || 0
        };

        setNetworkStats(newStats);

        // Add to activity chart
        setActivityData(prev => {
          const now = new Date();
          const newPoint = {
            time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            cores: newStats.total_cores,
            workers: newStats.workers_online
          };
          const updated = [...prev, newPoint].slice(-30);
          return updated;
        });
      } catch (err) {
        // Silent
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch leaderboards
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${API_URL}/leaderboard?limit=50`);
        const data = await res.json();
        if (data.success) setLeaderboard(data.leaderboard || []);
      } catch (err) {}
      setLeaderboardLoading(false);
    };

    const fetchAllianceLeaderboard = async () => {
      try {
        const res = await fetch(`${API_URL}/leaderboard/alliances?limit=50`);
        const data = await res.json();
        if (data.success) setAllianceLeaderboard(data.leaderboard || []);
      } catch (err) {}
      setAllianceLeaderboardLoading(false);
    };

    fetchLeaderboard();
    fetchAllianceLeaderboard();
    const interval = setInterval(() => {
      fetchLeaderboard();
      fetchAllianceLeaderboard();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginHash.trim()) {
      setError('Please enter your Account Hash');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: loginHash.trim() })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('avalw_account_hash', loginHash.trim());
        navigate('/dashboard');
      } else {
        setError(data.error || 'Account not found');
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  const handleCreateAccount = async (accountType = 'server') => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_type: accountType })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('avalw_account_hash', data.account_hash);
        localStorage.setItem('avalw_account_type', accountType);
        setNewCredentials({
          accountHash: data.account_hash,
          workerToken: data.worker_token,
          accountType
        });
        setShowWelcome(true);
        setShowLogin(false);
      } else {
        setError(data.error || 'Failed to create account');
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {}
  };

  const handleContinueToDashboard = () => {
    setShowWelcome(false);
    const type = newCredentials?.accountType || 'server';
    navigate(type === 'home' ? '/home-dashboard' : '/dashboard');
  };

  const formatNum = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toString();
  };

  // Calculate chart dimensions
  const getChartPath = () => {
    if (activityData.length < 2) return '';
    const maxCores = Math.max(...activityData.map(d => d.cores), 1);
    const width = 100;
    const height = 100;
    const points = activityData.map((d, i) => {
      const x = (i / (activityData.length - 1)) * width;
      const y = height - (d.cores / maxCores) * height * 0.8 - 10;
      return `${x},${y}`;
    });
    return `M${points.join(' L')}`;
  };

  const getChartFill = () => {
    if (activityData.length < 2) return '';
    const maxCores = Math.max(...activityData.map(d => d.cores), 1);
    const width = 100;
    const height = 100;
    const points = activityData.map((d, i) => {
      const x = (i / (activityData.length - 1)) * width;
      const y = height - (d.cores / maxCores) * height * 0.8 - 10;
      return `${x},${y}`;
    });
    return `M0,${height} L${points.join(' L')} L${width},${height} Z`;
  };

  return (
    <div className="lp-page">
      {/* Navigation */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-logo">
            <div className="lp-logo-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
            </div>
            <span className="lp-logo-text">AVALW</span>
            <span className="lp-logo-badge">Worker</span>
          </Link>

          <div className="lp-nav-links">
            <a href="#network" className="lp-nav-link">Network</a>
            <a href="#leaderboard" className="lp-nav-link">Leaderboard</a>
            <Link to="/alliances" className="lp-nav-link">Alliances</Link>
            <a href="#start" className="lp-nav-link">Get Started</a>
            <button className="lp-nav-btn" onClick={() => setShowLogin(true)}>
              Dashboard
            </button>
          </div>

          <button className={`lp-mobile-btn ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span><span></span><span></span>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lp-mobile-menu" onClick={() => setMobileMenuOpen(false)}>
            <div className="lp-mobile-inner" onClick={e => e.stopPropagation()}>
              <a href="#network" onClick={() => setMobileMenuOpen(false)}>Network</a>
              <a href="#leaderboard" onClick={() => setMobileMenuOpen(false)}>Leaderboard</a>
              <Link to="/alliances" onClick={() => setMobileMenuOpen(false)}>Alliances</Link>
              <a href="#start" onClick={() => setMobileMenuOpen(false)}>Get Started</a>
              <button className="lp-btn lp-btn-primary" onClick={() => { setMobileMenuOpen(false); setShowLogin(true); }}>Dashboard</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-gradient"></div>
          <div className="lp-hero-grid-pattern"></div>
        </div>

        <div className="lp-hero-content">
          <div className="lp-hero-left">
            <div className="lp-hero-badge">
              <span className="lp-badge-dot"></span>
              Distributed Cache Network
            </div>
            <h1 className="lp-hero-title">
              Power the Future of
              <span className="lp-gradient-text"> Eco-Friendly Search</span>
            </h1>
            <p className="lp-hero-desc">
              Transform your idle computing power into environmental impact.
              Join a global network of contributors reducing data center energy consumption.
            </p>
            <div className="lp-hero-actions">
              <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => handleCreateAccount('server')} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
                Start Contributing
              </button>
              <a href="#leaderboard" className="lp-btn lp-btn-outline lp-btn-lg">
                View Leaderboard
              </a>
            </div>
          </div>

          <div className="lp-hero-right">
            <div className="lp-hero-stats-card">
              <div className="lp-stats-header">
                <span className="lp-live-badge">
                  <span className="lp-live-dot"></span>
                  Live Network
                </span>
              </div>
              <div className="lp-stats-grid">
                <div className="lp-stat-item">
                  <div className="lp-stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                  </div>
                  <div className="lp-stat-info">
                    <span className="lp-stat-value">{networkStats.workers_online}</span>
                    <span className="lp-stat-label">Contributors</span>
                  </div>
                </div>
                <div className="lp-stat-item">
                  <div className="lp-stat-icon purple">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 20.41L18.41 19 15 15.59 13.59 17M7.5 8H11v5.59L5.59 19 7 20.41l6-6V8h3.5L12 3.5"/></svg>
                  </div>
                  <div className="lp-stat-info">
                    <span className="lp-stat-value">{networkStats.total_cores}</span>
                    <span className="lp-stat-label">Total Cores</span>
                  </div>
                </div>
                <div className="lp-stat-item">
                  <div className="lp-stat-icon green">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
                  </div>
                  <div className="lp-stat-info">
                    <span className="lp-stat-value">{networkStats.co2_total_kg}kg</span>
                    <span className="lp-stat-label">CO2 Saved</span>
                  </div>
                </div>
                <div className="lp-stat-item">
                  <div className="lp-stat-icon orange">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
                  </div>
                  <div className="lp-stat-info">
                    <span className="lp-stat-value">{Math.round(networkStats.total_memory_gb)}GB</span>
                    <span className="lp-stat-label">Network RAM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contribute Section */}
      <section className="lp-benefits-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge green">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              Why Contribute
            </div>
            <h2 className="lp-section-title">Benefits of Contributing</h2>
            <p className="lp-section-desc">Your idle resources help build a faster, greener web</p>
          </div>

          <div className="lp-benefits-grid">
            <div className="lp-benefit-card-large">
              <div className="lp-benefit-icon-large blue">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
              </div>
              <h3>Earn Core-Hours</h3>
              <p>Unlock higher API tiers automatically. The more you contribute, the more free API queries you get access to - up to 500K/day at Enterprise tier.</p>
              <div className="lp-benefit-highlight">Up to 500K queries/day</div>
            </div>

            <div className="lp-benefit-card-large">
              <div className="lp-benefit-icon-large gold">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>
              </div>
              <h3>Climb the Leaderboard</h3>
              <p>Get recognized in the Top 50 contributors. Earn badges from Starter to Legendary based on your contribution score. Consistency beats raw power!</p>
              <div className="lp-benefit-highlight">Top 50 visibility</div>
            </div>

            <div className="lp-benefit-card-large">
              <div className="lp-benefit-icon-large purple">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
              </div>
              <h3>Privacy Protected</h3>
              <p>AES-256-GCM encryption ensures you never see search queries or user data. You're contributing resources, not accessing private information.</p>
              <div className="lp-benefit-highlight">Zero query visibility</div>
            </div>

            <div className="lp-benefit-card-large">
              <div className="lp-benefit-icon-large eco">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
              </div>
              <h3>Minimal Resources</h3>
              <p>Only 1-5% CPU and 50-200MB RAM. Runs quietly in the background without impacting your work. Pause anytime you need full power.</p>
              <div className="lp-benefit-highlight">1-5% CPU usage</div>
            </div>
          </div>
        </div>
      </section>

      {/* Network Activity Section */}
      <section id="network" className="lp-network-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg>
              Real-Time Analytics
            </div>
            <h2 className="lp-section-title">Network Activity</h2>
            <p className="lp-section-desc">Live visualization of the distributed network performance</p>
          </div>

          <div className="lp-network-grid">
            {/* Main Chart Card */}
            <div className="lp-chart-card">
              <div className="lp-chart-header">
                <h3>Network Cores</h3>
                <div className="lp-chart-legend">
                  <span className="lp-legend-item">
                    <span className="lp-legend-dot blue"></span>
                    Last 5 minutes
                  </span>
                </div>
              </div>
              <div className="lp-chart-body" ref={chartRef}>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="lp-chart-svg">
                  <defs>
                    <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(99, 102, 241, 0.3)" />
                      <stop offset="100%" stopColor="rgba(99, 102, 241, 0)" />
                    </linearGradient>
                  </defs>
                  {activityData.length >= 2 && (
                    <>
                      <path d={getChartFill()} fill="url(#chartGradient)" />
                      <path d={getChartPath()} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </>
                  )}
                </svg>
                <div className="lp-chart-value">
                  <span className="lp-chart-num">{networkStats.total_cores}</span>
                  <span className="lp-chart-label">cores online</span>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="lp-network-stats">
              <div className="lp-network-stat-card eco">
                <div className="lp-nstat-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
                </div>
                <div className="lp-nstat-content">
                  <span className="lp-nstat-value">{networkStats.co2_today_kg}kg</span>
                  <span className="lp-nstat-label">CO2 Saved Today</span>
                </div>
                <div className="lp-nstat-badge">-70% Energy</div>
              </div>

              <div className="lp-network-stat-card blue">
                <div className="lp-nstat-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
                </div>
                <div className="lp-nstat-content">
                  <span className="lp-nstat-value">{formatNum(networkStats.lifetime_core_hours)}</span>
                  <span className="lp-nstat-label">Total Core-Hours</span>
                </div>
                <div className="lp-nstat-badge">Lifetime</div>
              </div>

              <div className="lp-network-stat-card purple">
                <div className="lp-nstat-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
                </div>
                <div className="lp-nstat-content">
                  <span className="lp-nstat-value">{formatNum(networkStats.total_cache_served)}</span>
                  <span className="lp-nstat-label">Cache Served</span>
                </div>
                <div className="lp-nstat-badge">Total</div>
              </div>
            </div>
          </div>

          {/* Eco Impact Row */}
          <div className="lp-eco-row">
            <div className="lp-eco-card">
              <div className="lp-eco-icon trees">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
              </div>
              <div className="lp-eco-value">{networkStats.trees_equivalent}</div>
              <div className="lp-eco-label">Trees Equivalent</div>
            </div>
            <div className="lp-eco-card">
              <div className="lp-eco-icon car">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
              </div>
              <div className="lp-eco-value">{networkStats.car_km_equivalent}km</div>
              <div className="lp-eco-label">Car Emissions Offset</div>
            </div>
            <div className="lp-eco-card">
              <div className="lp-eco-icon energy">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/></svg>
              </div>
              <div className="lp-eco-value">-70%</div>
              <div className="lp-eco-label">Server Energy Saved</div>
            </div>
            <div className="lp-eco-card">
              <div className="lp-eco-icon global">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
              </div>
              <div className="lp-eco-value">{networkStats.total_accounts}</div>
              <div className="lp-eco-label">Total Contributors</div>
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard Section */}
      <section id="leaderboard" className="lp-leaderboard-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge gold">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>
              Live Rankings
            </div>
            <h2 className="lp-section-title">Top Contributors</h2>
            <p className="lp-section-desc">Ranked by contribution score and impact</p>
          </div>

          {/* Tabs */}
          <div className="lp-tabs">
            <button className={`lp-tab ${activeTab === 'contributors' ? 'active' : ''}`} onClick={() => setActiveTab('contributors')}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              Contributors
            </button>
            <button className={`lp-tab ${activeTab === 'alliances' ? 'active' : ''}`} onClick={() => setActiveTab('alliances')}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
              Alliances
            </button>
          </div>

          {/* Contributors Leaderboard */}
          {activeTab === 'contributors' && (
            <div className="lp-leaderboard-content">
              {leaderboardLoading ? (
                <div className="lp-loading">
                  <div className="lp-spinner"></div>
                  <p>Loading leaderboard...</p>
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="lp-empty">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/></svg>
                  <h3>Be the First!</h3>
                  <p>Start contributing to claim the #1 spot</p>
                  <button className="lp-btn lp-btn-primary" onClick={() => handleCreateAccount('server')}>Start Contributing</button>
                </div>
              ) : (
                <div className="lp-podium-grid">
                  {/* Top 3 Podium */}
                  {leaderboard.slice(0, 3).map((user, index) => (
                    <div key={user.id} className={`lp-podium-card rank-${index + 1}`}>
                      <div className="lp-podium-rank">
                        {index === 0 && <div className="lp-crown"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/></svg></div>}
                        <span className="lp-rank-num">{index + 1}</span>
                      </div>
                      <div className="lp-podium-avatar" style={{ backgroundColor: user.badge?.color || '#6366f1' }}>
                        {user.badge?.icon || user.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <h4 className="lp-podium-name">{user.display_name || `User ${user.id?.slice(0, 6)}`}</h4>
                      <div className="lp-podium-badge">{user.badge?.name || 'Contributor'}</div>
                      <div className="lp-podium-score">{formatNum(user.contribution_score)}</div>
                      <div className="lp-podium-label">Score</div>
                      <div className="lp-podium-stats">
                        <div className="lp-pstat">
                          <span className="lp-pstat-val">{user.uptime_days || 0}d</span>
                          <span className="lp-pstat-lbl">Uptime</span>
                        </div>
                        <div className="lp-pstat">
                          <span className="lp-pstat-val">{formatNum(user.core_hours_total)}</span>
                          <span className="lp-pstat-lbl">Core-Hrs</span>
                        </div>
                      </div>
                      <div className={`lp-podium-status ${user.worker_status}`}>
                        <span className="lp-status-dot"></span>
                        {user.worker_status === 'online' ? 'Online' : 'Offline'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Full Top 50 Leaderboard Table */}
              {leaderboard.length > 3 && (
                <div className="lp-leaderboard-table-wrapper">
                  <table className="lp-leaderboard-table">
                    <thead>
                      <tr>
                        <th className="lp-th-rank">#</th>
                        <th className="lp-th-user">Contributor</th>
                        <th className="lp-th-score">Score</th>
                        <th className="lp-th-uptime">Uptime</th>
                        <th className="lp-th-cache">Cache</th>
                        <th className="lp-th-status">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.slice(3).map((user, index) => (
                        <tr key={user.id} className={`lp-tr ${user.worker_status}`}>
                          <td className="lp-td-rank">
                            <span className="lp-rank-badge">{index + 4}</span>
                          </td>
                          <td className="lp-td-user">
                            <div className="lp-user-cell">
                              <div className="lp-list-avatar" style={{ backgroundColor: user.badge?.color || '#94a3b8' }}>
                                {user.badge?.icon || user.display_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="lp-user-info">
                                <span className="lp-user-name">{user.display_name}</span>
                                <span className="lp-user-badge">{user.badge?.name || 'Starter'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="lp-td-score">
                            <span className="lp-score-value">{formatNum(user.contribution_score)}</span>
                          </td>
                          <td className="lp-td-uptime">{user.uptime_days || 0}d</td>
                          <td className="lp-td-cache">{formatNum(user.total_cache_served)}</td>
                          <td className="lp-td-status">
                            <span className={`lp-status-indicator ${user.worker_status}`}>
                              <span className="lp-status-dot"></span>
                              {user.worker_status === 'online' ? 'Online' : 'Offline'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Score Formula Info */}
              <div className="lp-score-formula">
                <div className="lp-formula-title">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                  Contribution Score = (Uptime Hours x 10) + Core-Hours + (Cache Served / 1000)
                </div>
                <p className="lp-formula-desc">Consistency wins! A 4-core CPU running 24/7 earns more than a 16-core running occasionally.</p>
              </div>

              <div className="lp-leaderboard-cta">
                <Link to="/leaderboard" className="lp-btn lp-btn-outline">
                  View Full Top 50
                </Link>
              </div>
            </div>
          )}

          {/* Alliances Leaderboard */}
          {activeTab === 'alliances' && (
            <div className="lp-leaderboard-content">
              {allianceLeaderboardLoading ? (
                <div className="lp-loading">
                  <div className="lp-spinner"></div>
                  <p>Loading alliances...</p>
                </div>
              ) : allianceLeaderboard.length === 0 ? (
                <div className="lp-empty">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3z"/></svg>
                  <h3>No Alliances Yet</h3>
                  <p>Create the first alliance!</p>
                  <Link to="/alliances/create" className="lp-btn lp-btn-primary">Create Alliance</Link>
                </div>
              ) : (
                <div className="lp-podium-grid alliances">
                  {allianceLeaderboard.slice(0, 3).map((alliance, index) => (
                    <Link to={`/alliance/${alliance.id}`} key={alliance.id} className={`lp-podium-card rank-${index + 1}`}>
                      <div className="lp-podium-rank">
                        {index === 0 && <div className="lp-crown"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/></svg></div>}
                        <span className="lp-rank-num">{index + 1}</span>
                      </div>
                      <div className="lp-podium-avatar alliance" style={{ backgroundColor: alliance.banner_color || '#8b5cf6' }}>
                        {alliance.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <h4 className="lp-podium-name">{alliance.name}</h4>
                      <div className="lp-podium-badge">Level {alliance.level}</div>
                      <div className="lp-podium-score">{formatNum(alliance.xp)}</div>
                      <div className="lp-podium-label">XP</div>
                      <div className="lp-podium-stats">
                        <div className="lp-pstat">
                          <span className="lp-pstat-val">{alliance.member_count}</span>
                          <span className="lp-pstat-lbl">Members</span>
                        </div>
                        <div className="lp-pstat eco">
                          <span className="lp-pstat-val">{alliance.co2_saved_kg}kg</span>
                          <span className="lp-pstat-lbl">CO2</span>
                        </div>
                      </div>
                      <div className="lp-podium-online">
                        <span className="lp-status-dot online"></span>
                        {alliance.online_count} online
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              <div className="lp-leaderboard-cta">
                <Link to="/alliances" className="lp-btn lp-btn-outline">
                  View All Alliances
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="lp-how-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
              How It Works
            </div>
            <h2 className="lp-section-title">Simple, Secure, Rewarding</h2>
            <p className="lp-section-desc">Join the network in under 2 minutes</p>
          </div>

          <div className="lp-flow">
            <div className="lp-flow-step">
              <div className="lp-flow-num">1</div>
              <div className="lp-flow-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              </div>
              <h4>Create Account</h4>
              <p>Get your unique hash and token instantly</p>
            </div>
            <div className="lp-flow-arrow">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
            </div>
            <div className="lp-flow-step">
              <div className="lp-flow-num">2</div>
              <div className="lp-flow-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
              </div>
              <h4>Run Worker</h4>
              <p>One command to start contributing</p>
            </div>
            <div className="lp-flow-arrow">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
            </div>
            <div className="lp-flow-step">
              <div className="lp-flow-num">3</div>
              <div className="lp-flow-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
              </div>
              <h4>Encrypted Cache</h4>
              <p>AES-256 protects all data</p>
            </div>
            <div className="lp-flow-arrow">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
            </div>
            <div className="lp-flow-step">
              <div className="lp-flow-num">4</div>
              <div className="lp-flow-icon eco">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/></svg>
              </div>
              <h4>Earn & Climb</h4>
              <p>Watch your score grow</p>
            </div>
          </div>

          <div className="lp-privacy-badges">
            <div className="lp-priv-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
              AES-256-GCM Encryption
            </div>
            <div className="lp-priv-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>
              Zero Query Visibility
            </div>
            <div className="lp-priv-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/></svg>
              RAM-Only Keys
            </div>
            <div className="lp-priv-badge">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              No Email Required
            </div>
          </div>
        </div>
      </section>

      {/* Free Search API Section */}
      <section className="lp-api-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge api">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/></svg>
              Free Search API
            </div>
            <h2 className="lp-section-title">Zero-Cost Web Search API</h2>
            <p className="lp-section-desc">Why pay $5-25 per 1,000 queries when you can contribute computing power instead?</p>
          </div>

          <div className="lp-api-comparison">
            <div className="lp-api-card others">
              <div className="lp-api-header">
                <h3>Traditional APIs</h3>
                <span className="lp-api-badge expensive">Expensive</span>
              </div>
              <div className="lp-api-price">
                <span className="lp-price-amount">$5 - $25</span>
                <span className="lp-price-period">per 1,000 queries</span>
              </div>
              <ul className="lp-api-features">
                <li className="negative">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  Google: $5 per 1,000 queries
                </li>
                <li className="negative">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  Bing: $7 per 1,000 queries
                </li>
                <li className="negative">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  SerpAPI: $15 per 1,000 queries
                </li>
                <li className="negative">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  Tracks your usage
                </li>
                <li className="negative">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  Credit card required
                </li>
              </ul>
            </div>

            <div className="lp-api-vs">VS</div>

            <div className="lp-api-card avalw">
              <div className="lp-api-header">
                <h3>AVALW API</h3>
                <span className="lp-api-badge free">Free Forever</span>
              </div>
              <div className="lp-api-price">
                <span className="lp-price-amount">$0</span>
                <span className="lp-price-period">pay with compute power</span>
              </div>
              <ul className="lp-api-features">
                <li className="positive">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  Up to 500K queries/day (Enterprise)
                </li>
                <li className="positive">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  No credit card needed
                </li>
                <li className="positive">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  Zero tracking, fully anonymous
                </li>
                <li className="positive">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  Earn tiers by contributing
                </li>
                <li className="positive">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  GDPR compliant by design
                </li>
              </ul>
              <button className="lp-btn lp-btn-primary lp-btn-full" onClick={() => handleCreateAccount('server')}>
                Start Free
              </button>
            </div>
          </div>

          <div className="lp-api-example">
            <h4>Simple REST API</h4>
            <div className="lp-code-block">
              <code>
                <span className="lp-code-method">GET</span> https://api.avalw.org/search?q=your+query&token=YOUR_TOKEN
              </code>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Security Section */}
      <section className="lp-privacy-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge security">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
              Zero-Knowledge Design
            </div>
            <h2 className="lp-section-title">Privacy as a Feature, Not an Afterthought</h2>
            <p className="lp-section-desc">Built from the ground up with cryptographic privacy guarantees</p>
          </div>

          <div className="lp-privacy-grid">
            <div className="lp-privacy-card">
              <div className="lp-privacy-icon hash">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
              </div>
              <h4>Hash-Based Identity</h4>
              <p>No email, no password, no PII. Your account is a cryptographic hash - anonymous by design.</p>
            </div>

            <div className="lp-privacy-card">
              <div className="lp-privacy-icon encrypt">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
              </div>
              <h4>AES-256-GCM Encryption</h4>
              <p>All cached data is encrypted. Workers store data they cannot read or decrypt.</p>
            </div>

            <div className="lp-privacy-card">
              <div className="lp-privacy-icon blind">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27z"/></svg>
              </div>
              <h4>Zero Query Visibility</h4>
              <p>Workers never see search queries. Only encrypted hashes travel through the network.</p>
            </div>

            <div className="lp-privacy-card">
              <div className="lp-privacy-icon ram">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15 9H9v6h6V9zm-2 4h-2v-2h2v2zm8-2V9h-2V7c0-1.1-.9-2-2-2h-2V3h-2v2h-2V3H9v2H7c-1.1 0-2 .9-2 2v2H3v2h2v2H3v2h2v2c0 1.1.9 2 2 2h2v2h2v-2h2v2h2v-2h2c1.1 0 2-.9 2-2v-2h2v-2h-2v-2h2z"/></svg>
              </div>
              <h4>RAM-Only Decryption</h4>
              <p>Encryption keys exist only in server memory. No persistent key storage anywhere.</p>
            </div>

            <div className="lp-privacy-card">
              <div className="lp-privacy-icon logs">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
              </div>
              <h4>No Search Logs</h4>
              <p>We don't log what you search. Ever. No analytics, no profiling, no ads.</p>
            </div>

            <div className="lp-privacy-card">
              <div className="lp-privacy-icon gdpr">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              </div>
              <h4>GDPR Compliant</h4>
              <p>We collect no personal data, so there's nothing to delete. Privacy by default.</p>
            </div>
          </div>

          <div className="lp-security-banner">
            <div className="lp-sec-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
            </div>
            <div className="lp-sec-content">
              <h4>What Workers Can NEVER See:</h4>
              <div className="lp-sec-items">
                <span>Your search queries</span>
                <span>Your IP address</span>
                <span>Result contents</span>
                <span>Any identifiable info</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API Tiers Section */}
      <section className="lp-tiers-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge tiers">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 10h12v2H4zm0-4h12v2H4zm0 8h8v2H4zm10 0v6l5-3z"/></svg>
              API Tiers
            </div>
            <h2 className="lp-section-title">Unlock More with Core-Hours</h2>
            <p className="lp-section-desc">The more you contribute, the more API access you earn</p>
          </div>

          <div className="lp-tiers-grid">
            <div className="lp-tier-card starter">
              <div className="lp-tier-header">
                <div className="lp-tier-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                </div>
                <h3>Starter</h3>
                <span className="lp-tier-req">0 core-hours</span>
              </div>
              <div className="lp-tier-limit">
                <span className="lp-tier-num">1,000</span>
                <span className="lp-tier-period">queries / day</span>
              </div>
              <ul className="lp-tier-features">
                <li>Basic search API</li>
                <li>Standard rate limits</li>
                <li>Community support</li>
              </ul>
              <div className="lp-tier-tag">Free to start</div>
            </div>

            <div className="lp-tier-card basic">
              <div className="lp-tier-header">
                <div className="lp-tier-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                </div>
                <h3>Basic</h3>
                <span className="lp-tier-req">100 core-hours</span>
              </div>
              <div className="lp-tier-limit">
                <span className="lp-tier-num">10,000</span>
                <span className="lp-tier-period">queries / day</span>
              </div>
              <ul className="lp-tier-features">
                <li>Everything in Starter</li>
                <li>Higher rate limits</li>
                <li>Priority queue</li>
              </ul>
              <div className="lp-tier-tag">~4 days contribution</div>
            </div>

            <div className="lp-tier-card pro">
              <div className="lp-tier-popular">Most Popular</div>
              <div className="lp-tier-header">
                <div className="lp-tier-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                </div>
                <h3>Pro</h3>
                <span className="lp-tier-req">500 core-hours</span>
              </div>
              <div className="lp-tier-limit">
                <span className="lp-tier-num">50,000</span>
                <span className="lp-tier-period">queries / day</span>
              </div>
              <ul className="lp-tier-features">
                <li>Everything in Basic</li>
                <li>Batch API access</li>
                <li>Webhook support</li>
              </ul>
              <div className="lp-tier-tag">~3 weeks contribution</div>
            </div>

            <div className="lp-tier-card business">
              <div className="lp-tier-header">
                <div className="lp-tier-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10z"/></svg>
                </div>
                <h3>Business</h3>
                <span className="lp-tier-req">2,000 core-hours</span>
              </div>
              <div className="lp-tier-limit">
                <span className="lp-tier-num">200,000</span>
                <span className="lp-tier-period">queries / day</span>
              </div>
              <ul className="lp-tier-features">
                <li>Everything in Pro</li>
                <li>Dedicated endpoints</li>
                <li>SLA guarantee</li>
              </ul>
              <div className="lp-tier-tag">~3 months contribution</div>
            </div>

            <div className="lp-tier-card enterprise">
              <div className="lp-tier-header">
                <div className="lp-tier-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm6 9.09c0 4-2.55 7.7-6 8.83-3.45-1.13-6-4.82-6-8.83v-4.7l6-2.25 6 2.25v4.7z"/></svg>
                </div>
                <h3>Enterprise</h3>
                <span className="lp-tier-req">10,000 core-hours</span>
              </div>
              <div className="lp-tier-limit">
                <span className="lp-tier-num">500,000</span>
                <span className="lp-tier-period">queries / day</span>
              </div>
              <ul className="lp-tier-features">
                <li>Everything in Business</li>
                <li>Custom rate limits</li>
                <li>Direct support</li>
              </ul>
              <div className="lp-tier-tag">~1 year contribution</div>
            </div>
          </div>

          <div className="lp-tiers-note">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
            <p><strong>Core-hours calculation:</strong> CPU cores × active hours. A 4-core CPU running 24h = 96 core-hours/day.</p>
          </div>
        </div>
      </section>

      {/* For Developers Section */}
      <section className="lp-dev-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge dev">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
              For Developers
            </div>
            <h2 className="lp-section-title">Built for Developers & Businesses</h2>
            <p className="lp-section-desc">Integrate search without compromising your users' privacy</p>
          </div>

          <div className="lp-dev-grid">
            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>
              </div>
              <h4>Zero User Tracking</h4>
              <p>Unlike Google/Bing APIs, we don't profile your users or their searches.</p>
            </div>

            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/></svg>
              </div>
              <h4>Encrypted Cache</h4>
              <p>Results stored with AES-256-GCM. Even our network can't read the data.</p>
            </div>

            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/></svg>
              </div>
              <h4>Pay with Compute</h4>
              <p>No credit cards, no invoices. Contribute CPU power, get API access.</p>
            </div>

            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
              </div>
              <h4>No Query Logs</h4>
              <p>We don't store search history. Your users' queries stay private.</p>
            </div>

            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93z"/></svg>
              </div>
              <h4>Global CDN</h4>
              <p>Distributed cache network for fast responses anywhere in the world.</p>
            </div>

            <div className="lp-dev-card">
              <div className="lp-dev-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
              </div>
              <h4>Simple Integration</h4>
              <p>REST API with JSON responses. Works with any language or framework.</p>
            </div>
          </div>

          <div className="lp-dev-cta">
            <button className="lp-btn lp-btn-primary" onClick={() => handleCreateAccount('server')}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/></svg>
              Get API Access
            </button>
          </div>
        </div>
      </section>

      {/* Get Started Section */}
      <section id="start" className="lp-start-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-section-badge green">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
              Quick Start
            </div>
            <h2 className="lp-section-title">Start in 2 Minutes</h2>
            <p className="lp-section-desc">Choose your setup and start contributing today</p>
          </div>

          <div className="lp-start-grid">
            {/* Server Option */}
            <div className="lp-start-card server">
              <div className="lp-start-header">
                <div className="lp-start-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
                </div>
                <div className="lp-start-title">
                  <h3>Server / VPS</h3>
                  <span>Maximum contribution</span>
                </div>
              </div>
              <div className="lp-start-code">
                <div className="lp-code-line">
                  <span className="lp-code-comment"># Install globally</span>
                </div>
                <div className="lp-code-line">
                  <code>npm i -g @avalw/search-worker</code>
                </div>
                <div className="lp-code-line">
                  <span className="lp-code-comment"># Run with your token</span>
                </div>
                <div className="lp-code-line">
                  <code>avalw-worker --token YOUR_TOKEN</code>
                </div>
              </div>
              <button className="lp-btn lp-btn-primary lp-btn-full" onClick={() => handleCreateAccount('server')} disabled={loading}>
                {loading ? 'Creating...' : 'Get Server Token'}
              </button>
            </div>

            {/* Home Option */}
            <div className="lp-start-card home">
              <div className="lp-start-header">
                <div className="lp-start-icon home">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                </div>
                <div className="lp-start-title">
                  <h3>Home PC</h3>
                  <span>Casual contribution</span>
                </div>
              </div>
              <div className="lp-start-code">
                <div className="lp-code-line">
                  <span className="lp-code-comment"># Quick start (no install)</span>
                </div>
                <div className="lp-code-line">
                  <code>npx @avalw/search-worker --token YOUR_TOKEN</code>
                </div>
                <div className="lp-code-line">
                  <span className="lp-code-comment"># Or use Cronos Browser</span>
                </div>
                <div className="lp-code-line">
                  <code>cronos.avalw.com</code>
                </div>
              </div>
              <button className="lp-btn lp-btn-home lp-btn-full" onClick={() => handleCreateAccount('home')} disabled={loading}>
                {loading ? 'Creating...' : 'Get Home Token'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Alliances Promo */}
      <section className="lp-alliance-section">
        <div className="lp-container">
          <div className="lp-alliance-card">
            <div className="lp-alliance-content">
              <div className="lp-section-badge purple">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3z"/></svg>
                Alliances
              </div>
              <h2>Stronger Together</h2>
              <p>Join forces with up to 100 contributors. Pool resources, climb leaderboards, and multiply your environmental impact as a team.</p>
              <div className="lp-alliance-stats">
                <div className="lp-astat">
                  <span className="lp-astat-val">100</span>
                  <span className="lp-astat-lbl">Max Members</span>
                </div>
                <div className="lp-astat">
                  <span className="lp-astat-val">50</span>
                  <span className="lp-astat-lbl">Levels</span>
                </div>
                <div className="lp-astat eco">
                  <span className="lp-astat-val">10x</span>
                  <span className="lp-astat-lbl">Impact</span>
                </div>
              </div>
              <div className="lp-alliance-actions">
                <Link to="/alliances" className="lp-btn lp-btn-primary">Browse Alliances</Link>
                <Link to="/alliances/create" className="lp-btn lp-btn-outline">Create Alliance</Link>
              </div>
            </div>
            <div className="lp-alliance-visual">
              <div className="lp-alliance-orb"></div>
              <div className="lp-alliance-avatars">
                <div className="lp-a-avatar a1">A</div>
                <div className="lp-a-avatar a2">B</div>
                <div className="lp-a-avatar a3">C</div>
                <div className="lp-a-avatar a4">D</div>
                <div className="lp-a-avatar a5">+96</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-cta-section">
        <div className="lp-container">
          <div className="lp-cta-card">
            <h2>Ready to Make an Impact?</h2>
            <p>Join {networkStats.total_accounts}+ contributors worldwide. Start earning rewards while helping the environment.</p>
            <div className="lp-cta-actions">
              <button className="lp-btn lp-btn-white lp-btn-lg" onClick={() => handleCreateAccount('server')} disabled={loading}>
                Start Contributing
              </button>
              <a href="https://cronos.avalw.com" target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn-outline-white lp-btn-lg">
                Get Cronos Browser
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <div className="lp-footer-logo">AVALW Worker</div>
              <p>Distributed cache network for eco-friendly search</p>
            </div>
            <div className="lp-footer-links">
              <h4>Documentation</h4>
              <Link to="/transparency">Transparency</Link>
              <Link to="/protocol">Protocol</Link>
              <Link to="/security">Security</Link>
            </div>
            <div className="lp-footer-links">
              <h4>Resources</h4>
              <a href="https://npmjs.com/package/@avalw/search-worker" target="_blank" rel="noopener noreferrer">NPM Package</a>
              <a href="https://avalw.org" target="_blank" rel="noopener noreferrer">Search Engine</a>
              <a href="https://avalw.com" target="_blank" rel="noopener noreferrer">Platform</a>
            </div>
            <div className="lp-footer-links">
              <h4>Contact</h4>
              <a href="mailto:contact@avalw.com">contact@avalw.com</a>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <p>AVALW Network. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div className="lp-modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="lp-modal" onClick={e => e.stopPropagation()}>
            <button className="lp-modal-close" onClick={() => setShowLogin(false)}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
            <h2>Access Dashboard</h2>
            <p>Enter your Account Hash</p>
            {error && <div className="lp-modal-error">{error}</div>}
            <form onSubmit={handleLogin}>
              <input type="text" value={loginHash} onChange={e => setLoginHash(e.target.value)} placeholder="Your Account Hash" className="lp-modal-input" />
              <button type="submit" className="lp-btn lp-btn-primary lp-btn-full" disabled={loading}>
                {loading ? 'Logging in...' : 'Access Dashboard'}
              </button>
            </form>
            <div className="lp-modal-divider">or create new account</div>
            <div className="lp-modal-options">
              <button className="lp-btn lp-btn-secondary" onClick={() => handleCreateAccount('server')} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1z"/></svg>
                Server
              </button>
              <button className="lp-btn lp-btn-home" onClick={() => handleCreateAccount('home')} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                Home PC
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal */}
      {showWelcome && newCredentials && (
        <div className="lp-modal-overlay welcome">
          <div className="lp-modal welcome" onClick={e => e.stopPropagation()}>
            <div className="lp-welcome-header">
              <div className="lp-welcome-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              </div>
              <h2>Welcome to AVALW!</h2>
              <p>Save these credentials securely</p>
            </div>
            <div className="lp-credentials">
              <div className="lp-cred-item">
                <label>Account Hash <span>(login)</span></label>
                <div className="lp-cred-box">
                  <code>{newCredentials.accountHash}</code>
                  <button className={`lp-copy-btn ${copiedField === 'hash' ? 'copied' : ''}`} onClick={() => copyToClipboard(newCredentials.accountHash, 'hash')}>
                    {copiedField === 'hash' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="lp-cred-item">
                <label>Worker Token <span>(run worker)</span></label>
                <div className="lp-cred-box">
                  <code>{newCredentials.workerToken}</code>
                  <button className={`lp-copy-btn ${copiedField === 'token' ? 'copied' : ''}`} onClick={() => copyToClipboard(newCredentials.workerToken, 'token')}>
                    {copiedField === 'token' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
            <div className="lp-warning">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
              <span>Cannot be recovered if lost!</span>
            </div>
            <div className="lp-quickstart">
              <label>Quick Start Command</label>
              <div className="lp-cred-box">
                <code>npx @avalw/search-worker --token {newCredentials.workerToken.substring(0, 16)}...</code>
                <button className={`lp-copy-btn ${copiedField === 'cmd' ? 'copied' : ''}`} onClick={() => copyToClipboard(`npx @avalw/search-worker --token ${newCredentials.workerToken}`, 'cmd')}>
                  {copiedField === 'cmd' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button className="lp-btn lp-btn-primary lp-btn-full lp-btn-lg" onClick={handleContinueToDashboard}>
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Landing;
